/*
 This file is part of GNU Taler
 (C) 2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

import type { VNode } from "preact";
import { useState } from "preact/hooks";
import type { AccessToken, AmountString } from "@gnu-taler/taler-util";
import {
  useMerchantConfig,
  useInventory,
  useCategories,
  useOrders,
  useOrderDetails,
  useTokenFamilies,
} from "../api/hooks.js";
import { unwrap } from "../api/failure.js";
import {
  executeCreateOrder,
  remainingRefundAmount,
  validateRefundAmount,
} from "../utils/orders.js";
import { resolveOrderPayment } from "../api/hooks/useOrders.js";
import { merchantClient } from "../api/client.js";
import { session, currentBackendUrl } from "../stores/session.js";
import { PosScreen, type PosScreenProps } from "../screens/PosScreen.js";
import { useTranslation } from "../context/translation.js";

export interface PosRefundDetails {
  orderId: string;
  remaining: AmountString;
}

/** Load the selected order and calculate its authoritative refund remainder. */
export async function loadPosRefundDetails(
  config: { rootUrl: URL; account?: string },
  token: AccessToken,
  orderId: string,
): Promise<PosRefundDetails | undefined> {
  if (!orderId) return undefined;
  const client = merchantClient(config);
  const body = unwrap(await client.getOrderDetails(token, orderId));
  if (body.order_status !== "paid") return undefined;
  const total = resolveOrderPayment(body).amount;
  const remaining = remainingRefundAmount(total, body.refund_amount);
  if (!remaining) return undefined;
  return { orderId, remaining };
}

/** Re-read before refunding so a stale till cannot over-refund the order. */
export async function issuePosRefund(
  config: { rootUrl: URL; account?: string },
  token: AccessToken,
  orderId: string,
  requested: string,
  reason: string,
): Promise<boolean> {
  const detail = await loadPosRefundDetails(config, token, orderId);
  const refund = validateRefundAmount(requested, detail?.remaining);
  if (!refund) return false;
  const client = merchantClient(config);
  unwrap(await client.addRefund(token, orderId, { refund, reason }));
  return true;
}

export function PosRoute(props: PosScreenProps): VNode {
  const { t } = useTranslation();
  // The till decides which order it is charging, so the route follows it.
  const [activeOrderId, setActiveOrderId] = useState<string | undefined>(props.activeOrderId);

  const { primaryCurrency, config, resource: configResource } = useMerchantConfig();
  const { products, isLoading: isProductsLoading, resource: productsResource } = useInventory();
  const { categories, resource: categoriesResource } = useCategories();
  const { orders, isLoading: isOrdersLoading, resource: ordersResource, mutate: refreshOrders } = useOrders();
  const { order, resource: orderResource, deleteOrder } = useOrderDetails(activeOrderId);
  const { tokenFamilies, semanticDetailsComplete, resource: tokenFamiliesResource } = useTokenFamilies();

  const token = session.value.token;
  const clientConfig = {
    rootUrl: new URL(currentBackendUrl.value),
    account: session.value.account,
  };

  return (
    <PosScreen
      {...props}
      products={products ?? []}
      categories={categories ?? []}
      orders={orders ?? []}
      tokenFamilies={tokenFamilies}
      semanticDetailsComplete={semanticDetailsComplete}
      tokenFamiliesResource={tokenFamiliesResource}
      primaryCurrency={primaryCurrency}
      config={config}
      configResource={configResource}
      productsResource={productsResource}
      categoriesResource={categoriesResource}
      ordersResource={ordersResource}
      orderResource={orderResource}
      isProductsLoading={isProductsLoading}
      isOrdersLoading={isOrdersLoading}
      fetchedOrder={order}
      onActiveOrderChange={setActiveOrderId}
      onCreateOrder={
        token
          ? async (request) => {
              const res = await executeCreateOrder(
                { rootUrl: new URL(currentBackendUrl.value), account: session.value.account },
                token,
                request,
              );
              return unwrap(res).order_id;
            }
          : undefined
      }
      onLoadRefundOrder={token
        ? (orderId) => loadPosRefundDetails(clientConfig, token, orderId)
        : undefined}
      onIssueRefund={token
        ? async (orderId, amount, reason) => {
            const issued = await issuePosRefund(
              clientConfig,
              token,
              orderId,
              amount,
              reason,
            );
            if (!issued) throw new Error(t`Failed to issue refund.`);
            await refreshOrders();
          }
        : undefined}
      onCancelOrder={token && activeOrderId
        ? async (orderId) => {
            if (orderId !== activeOrderId) {
              throw new Error(t`The active order changed before it could be canceled.`);
            }
            await deleteOrder(false);
            await refreshOrders();
          }
        : undefined}
    />
  );
}
